import {
  HelpCircle,
  Home,
  Users,
  Building2,
  FileText,
  Bell,
  BarChart3,
  ChartNoAxesCombined,
  Shield,
  Database,
  Settings,
  FolderOpen,
  ScrollText,
  Inbox,
  type LucideIcon,
} from "lucide-react";
import { Routes, Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import type { UserRole } from "@/types/user";
import { UserResponseDtoRole } from "@/types/api";

export interface AdminNavSubItem {
  title: string;
  url: string;
  icon: LucideIcon;
  roles?: UserRole[];
  permission?: string;
  external?: boolean;
}

export interface AdminNavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  roles?: UserRole[];
  permission?: string;
  external?: boolean;
  items?: AdminNavSubItem[];
}

export const navMain: AdminNavItem[] = [
  {
    title: "لوحة التحكم",
    url: appPath(),
    icon: Home,
  },
  {
    title: "المستخدمون والصلاحيات",
    url: "#",
    icon: Users,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "users:read",
    items: [
      {
        title: "جميع المستخدمين",
        url: appPath(Pages.USERS),
        icon: Users,
        roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
        permission: "users:read",
      },
      {
        title: "إضافة مستخدم",
        url: appPath(Pages.USERS, "new"),
        icon: Users,
        roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
        permission: "users:create",
      },
      {
        title: "الصلاحيات",
        url: appPath(Pages.USERS, "permissions"),
        icon: Shield,
        roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
        permission: "users:manage-editor-permissions",
      },
      {
        title: "إدارة الأدوار",
        url: appPath(Pages.ROLES),
        icon: Shield,
        roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
        permission: "users:manage-permissions",
      },
    ],
  },
  {
    // View-only for SUB_ADMIN (the list endpoint needs no users:* permission); edits stay
    // gated server-side on users:* / system:manage-settings.
    title: "الهيكل التنظيمي",
    url: appPath(Routes.DEPARTMENTS),
    icon: Building2,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
  },
  {
    title: "المحتوى",
    url: "#",
    icon: FileText,
    permission: "content:edit",
    items: [
      {
        title: "الصفحات",
        url: appPath("pages"),
        icon: FileText,
        permission: "content:edit",
      },
      {
        title: "المستندات",
        url: appPath(Routes.DOCUMENTS),
        icon: FolderOpen,
        permission: "documents:read",
      },
      {
        // faq:manage is a single global permission with no department tier,
        // SUPER_ADMIN-only by baseline — the same shape as the two dataset screens
        // that used to sit beside this one, now under «الرسومات البيانية».
        title: "الأسئلة الشائعة",
        url: appPath(Routes.FAQS),
        icon: HelpCircle,
        permission: "faq:manage",
      },
    ],
  },
  {
    /**
     * The two dataset screens, moved out of «المحتوى» into a group of their own.
     * They are not pages or documents: each one publishes a FIGURES file behind a
     * chart the public site draws, on its own global permission.
     *
     * 🔑 NO `permission` ON THE GROUP, DELIBERATELY. app-sidebar shows a group when at
     * least one CHILD survives the filter and never reads the parent's own permission
     * (that is why «المحتوى» carries a `content:edit` nothing consults). Putting
     * `obs:manage` here would read as correct and would hide «الإيفادات الرسمية» from
     * somebody who holds only `delegations:manage` — the two are independent grants and
     * one must never speak for the other.
     */
    title: "الرسومات البيانية",
    url: "#",
    icon: ChartNoAxesCombined,
    items: [
      {
        // Only SUPER_ADMIN holds obs:manage by baseline, so this stays hidden
        // for everyone else — the sidebar filters on the permission, not the role.
        title: "تقارير الموازنة المفتوحة",
        url: appPath(Routes.OBS_REPORTS),
        icon: BarChart3,
        permission: "obs:manage",
      },
      {
        // Same shape as OBS above and next to it for the same reason: a single global
        // permission with no department tier, SUPER_ADMIN-only by baseline. The screen
        // it manages replaced a Power BI embed the home page used to link to.
        //
        // 🔄 ScrollText, not Plane. A plane illustrates the TRIP; what this screen manages is the
        // ministerial order authorising it — «الأمر الوزاري» is a column in the workbook itself —
        // and the records outlive the travel. FileText would have been the plainer "paper" and is
        // taken twice already (المحتوى, الصفحات); a glyph repeated inside one sidebar reads as a
        // mistake, which is the same reason the group above carries ChartNoAxesCombined rather
        // than the BarChart3 its own child does.
        title: "الإيفادات الرسمية",
        url: appPath(Routes.DELEGATIONS),
        icon: ScrollText,
        permission: "delegations:manage",
      },
    ],
  },
  {
    title: "الإشعارات",
    url: appPath(Pages.NOTIFICATIONS),
    icon: Bell,
    roles: [
      UserResponseDtoRole.SUPER_ADMIN,
      UserResponseDtoRole.SUB_ADMIN,
      UserResponseDtoRole.EDITOR,
    ],
    permission: "system:view-dashboard",
  },
  {
    title: "التقارير والتحليلات",
    url: appPath(Pages.ANALYTICS),
    icon: BarChart3,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "system:view-analytics",
  },
  {
    title: "الطلبات والاستفسارات",
    url: appPath(Routes.SUBMISSIONS),
    icon: Inbox,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "system:view-analytics",
  },
  {
    title: "سجل التدقيق",
    url: appPath(Routes.AUDIT_LOGS),
    icon: Shield,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "system:view-audit-logs",
  },
  {
    title: "النسخ الاحتياطي والاستعادة",
    url: appPath(Routes.BACKUPS),
    icon: Database,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "system:backup-restore",
  },
  {
    title: "الإعدادات",
    url: appPath(Pages.SETTINGS),
    icon: Settings,
    roles: [UserResponseDtoRole.SUPER_ADMIN, UserResponseDtoRole.SUB_ADMIN],
    permission: "system:manage-settings",
  },
];

const DEFAULT_PAGE_TITLE = "لوحة التحكم";

/** Page-specific titles that differ from sidebar nav labels */
const EXACT_TITLE_OVERRIDES: Record<string, string> = {
  [appPath(Pages.SETTINGS)]: "إعدادات المنصة",
  [appPath(Pages.ROLES)]: "إدارة الأدوار",
  [appPath(Routes.DOCUMENTS)]: "إدارة المستندات",
};

const PATH_PATTERN_TITLE_OVERRIDES: Array<{
  test: (pathname: string) => boolean;
  title: string;
}> = [
  {
    test: (pathname) => /^\/users\/[^/]+\/edit$/.test(pathname),
    title: "تعديل مستخدم",
  },
  {
    test: (pathname) => /^\/roles\/[^/]+\/edit$/.test(pathname),
    title: "تعديل دور",
  },
  {
    test: (pathname) => pathname === appPath(Pages.ROLES, "new"),
    title: "إنشاء دور جديد",
  },
  {
    // One title for all six steps of the create wizard — the step strip on the
    // page already says which one, and a header that renamed itself every step
    // would compete with it.
    test: (pathname) =>
      new RegExp(
        `^${appPath(Routes.OBS_REPORTS, "new")}(?:/[^/]+)?/?$`,
      ).test(pathname),
    title: "تقرير موازنة جديد",
  },
];

function collectNavEntries(
  items: AdminNavItem[],
): Array<{ url: string; title: string }> {
  const entries: Array<{ url: string; title: string }> = [];

  for (const item of items) {
    if (item.url !== "#") {
      entries.push({ url: item.url, title: item.title });
    }
    if (item.items) {
      for (const sub of item.items) {
        entries.push({ url: sub.url, title: sub.title });
      }
    }
  }

  return entries;
}

const navEntries = collectNavEntries(navMain);

export function getPageTitle(pathname: string, search = ""): string {
  if (EXACT_TITLE_OVERRIDES[pathname]) {
    return EXACT_TITLE_OVERRIDES[pathname]!;
  }

  for (const { test, title } of PATH_PATTERN_TITLE_OVERRIDES) {
    if (test(pathname)) return title;
  }

  const adminRoot = appPath();

  const pathMatches = navEntries.filter(({ url }) => {
    if (url === "#") return false;
    const [urlPath] = url.split("?");
    if (urlPath === adminRoot) return pathname === adminRoot;
    return pathname === urlPath || pathname.startsWith(`${urlPath}/`);
  });

  if (pathMatches.length === 0) return DEFAULT_PAGE_TITLE;

  const searchMatches = pathMatches.filter(({ url }) => {
    const [, urlSearch = ""] = url.split("?");
    return `?${urlSearch}` === search || (urlSearch === "" && search === "");
  });

  const candidates = searchMatches.length > 0 ? searchMatches : pathMatches;

  return candidates.reduce((best, entry) =>
    entry.url.length > best.url.length ? entry : best,
  ).title;
}
